<template>
    <!--
        Lightweight SVG icon dispatcher for the TAG portal refresh design.
        Renders one of the named icons inline; `currentColor` lets the parent
        control stroke colour via CSS `color` rule.

        Lifted from /tmp/tag-portal-refresh/tag-portal/project/ui.jsx (the
        designer's React prototype). Kept verbatim so cross-referencing the
        prototype stays trivial — when you add a new icon there, mirror the
        entry in PATHS below.
    -->
    <svg
        :width="resolvedSize"
        :height="resolvedSize"
        :style="iconStyle"
        viewBox="0 0 24 24"
        :fill="isFill ? 'currentColor' : 'none'"
        :stroke="isFill ? 'none' : 'currentColor'"
        :stroke-width="isFill ? undefined : strokeWidth"
        stroke-linecap="round"
        stroke-linejoin="round"
        v-html="iconHtml"
    />
</template>

<script>
// Stroke-paint icons (16px viewbox standardized to 24).
const STROKE = {
    search:     '<circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/>',
    plus:       '<path d="M12 5v14M5 12h14"/>',
    filter:     '<path d="M3 6h18M6 12h12M10 18h4"/>',
    sort:       '<path d="M3 7h13M3 12h9M3 17h5"/><path d="m17 14 3 3 3-3M20 7v10"/>',
    chevDown:   '<path d="m6 9 6 6 6-6"/>',
    chevRight:  '<path d="m9 6 6 6-6 6"/>',
    chevLeft:   '<path d="m15 6-6 6 6 6"/>',
    x:          '<path d="M18 6 6 18M6 6l12 12"/>',
    check:      '<path d="M20 6 9 17l-5-5"/>',
    user:       '<circle cx="12" cy="8" r="4"/><path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8"/>',
    building:   '<rect x="4" y="3" width="16" height="18" rx="1.5"/><path d="M9 8h.01M15 8h.01M9 12h.01M15 12h.01M9 16h.01M15 16h.01"/>',
    briefcase:  '<rect x="2.5" y="7" width="19" height="13" rx="2"/><path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2M2.5 13h19"/>',
    receipt:    '<path d="M4 3v18l2.5-1.5L9 21l2.5-1.5L14 21l2.5-1.5L19 21l1-.5V3l-1-.5L16.5 4 14 2.5 11.5 4 9 2.5 6.5 4 4 3Z"/><path d="M8 8h8M8 12h8M8 16h5"/>',
    trending:   '<path d="m3 17 6-6 4 4 8-8"/><path d="M14 7h7v7"/>',
    spark:      '<path d="m12 3 1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3Z"/>',
    shield:     '<path d="M12 3 4 6v6c0 5 3.5 8.5 8 9 4.5-.5 8-4 8-9V6l-8-3Z"/>',
    bell:       '<path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10 21a2 2 0 0 0 4 0"/>',
    download:   '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="m7 10 5 5 5-5M12 15V3"/>',
    logout:     '<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><path d="m16 17 5-5-5-5M21 12H9"/>',
    columns:    '<rect x="3" y="4" width="18" height="16" rx="2"/><path d="M9 4v16M15 4v16"/>',
    edit:       '<path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z"/>',
    trash:      '<path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>',
    arrowUp:    '<path d="M12 19V5M5 12l7-7 7 7"/>',
    arrowDown:  '<path d="M12 5v14M5 12l7 7 7-7"/>',
    calendar:   '<rect x="3" y="4.5" width="18" height="17" rx="2"/><path d="M8 2.5v4M16 2.5v4M3 10h18"/>',
    // Mobile-specific glyphs from the variant-A handoff.
    sliders:    '<path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6"/>',
    menu:       '<path d="M3 6h18M3 12h18M3 18h18"/>',
};
// Fill-paint icons (rendered with fill="currentColor" instead of stroke).
const FILL = {
    more: '<circle cx="5" cy="12" r="1.6"/><circle cx="12" cy="12" r="1.6"/><circle cx="19" cy="12" r="1.6"/>',
    bolt: '<path d="M13 2 3 14h7l-1 8 10-12h-7l1-8Z"/>',
    // X-on-circle (clear-input button). Rendered as solid circle + hollow X.
    xCircle: '<circle cx="12" cy="12" r="10"/><path d="m15 9-6 6M9 9l6 6" stroke="#fff" stroke-width="2.2" fill="none" stroke-linecap="round"/>',
};

export default {
    name: "Icon",
    props: {
        name: { type: String, required: true },
        size: { type: [Number, String], default: 14 },
    },
    computed: {
        resolvedSize() {
            const n = Number(this.size);
            return Number.isFinite(n) ? n : 14;
        },
        iconStyle() {
            const size = `${this.resolvedSize}px`;

            return {
                width: size,
                height: size,
                flexShrink: 0,
                display: 'block',
            };
        },
        isFill() {
            return Object.prototype.hasOwnProperty.call(FILL, this.name);
        },
        iconHtml() {
            if (this.isFill) return FILL[this.name];
            return STROKE[this.name] || '';
        },
        strokeWidth() {
            // chevDown/Right/Left + check + arrows use heavier stroke per
            // designer's prototype. The rest sit at 1.8 unless they're
            // glyph-style icons (plus = 2.2, x = 2.2, etc.).
            if (this.name === 'check' || this.name.startsWith('arrow')) return 3;
            if (['plus', 'x', 'chevDown', 'chevRight', 'chevLeft'].includes(this.name)) return 2.2;
            if (['search', 'sort', 'filter', 'sliders', 'menu'].includes(this.name)) return 2;
            return 1.8;
        },
    },
};
</script>
